// src/pages/AdminPage/modals/CreateQuestionModal.tsx
import { useState, useEffect } from "react";
import { X, CheckCircle2, Loader2, Plus, Trash2 } from "lucide-react";
import { adminAPI } from "../utils/adminApi";
import { QuestionType } from "../utils/adminTypes";

type Lang = "oz" | "uz" | "ru";

const QUESTION_TYPES: { key: QuestionType; label: string }[] = [
  { key: QuestionType.ODDIY, label: "Oddiy" },
  { key: QuestionType.SONLI, label: "Sonli" },
  { key: QuestionType.KALIT_SOZLI, label: "Kalit so'zli" },
  { key: QuestionType.MURAKKAB, label: "Murakkab" },
];
interface TopicItem {
  id: number;
  bob?: string | number; // Bob ba'zida string yoki number kelishi mumkin
  count?: number;
  name_uz: string;
  name_oz: string;
  name_ru: string;
  video_url?: string | null;
  video_thumbnail?: string | null;
}
const LANGS: { key: Lang; label: string }[] = [
  { key: "oz", label: "O'zbekcha" },
  { key: "uz", label: "Ўзбекча" },
  { key: "ru", label: "Русский" },
];

const MIN_CHOICES = 2;
const MAX_CHOICES = 6;

interface CreateQuestionModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

interface ChoiceForm {
  // Frontend-only ID (React key uchun) — backend'ga ketmaydi
  _key: string;
  text_oz: string;
  text_uz: string;
  text_ru: string;
}

let keyCounter = 0;
const makeKey = () => `c_${Date.now()}_${keyCounter++}`;

const makeChoice = (): ChoiceForm => ({
  _key: makeKey(),
  text_oz: "",
  text_uz: "",
  text_ru: "",
});

interface QuestionForm {
  question_oz: string;
  question_uz: string;
  question_ru: string;
  explanation_oz: string;
  explanation_uz: string;
  explanation_ru: string;
  correctChoiceIdx: number;
  choices: ChoiceForm[];
  topicId: number | null;
  questionType: QuestionType;
  keywords: string[];
}

const emptyForm = (): QuestionForm => ({
  question_oz: "",
  question_uz: "",
  question_ru: "",
  explanation_oz: "",
  explanation_uz: "",
  explanation_ru: "",
  correctChoiceIdx: 0,
  choices: [makeChoice(), makeChoice(), makeChoice(), makeChoice()],
  topicId: null,
  questionType: QuestionType.ODDIY,
  keywords: [],
});

export default function CreateQuestionModal({
  open,
  onClose,
  onCreated,
  onError,
  onSuccess,
}: CreateQuestionModalProps) {
  const [activeLang, setActiveLang] = useState<Lang>("oz");
  const [form, setForm] = useState<QuestionForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [topics, setTopics] = useState<TopicItem[]>([]);
  // Qidiruv va ochib-yopish uchun statelar
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [keywordInput, setKeywordInput] = useState("");

  // Tanlangan mavzuning obyektini srazu topib olish uchun helper
  const selectedTopic = topics.find((t) => t.id === form.topicId);

  // Faqat qidirilgan matnga mos keladigan mavzularni filtrlash
  const filteredTopics = topics.filter((t) => {
    const name = `${t.name_oz}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });
  useEffect(() => {
    if (open) {
      adminAPI
        .getTopicsForSelect("")
        .then((res: any) => {
          setTopics(res.data || res);
        })
        .catch((err) => console.error("Mavzular yuklanmadi, bro:", err));
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setForm(emptyForm());
      setActiveLang("oz");
      setKeywordInput("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  if (!open) return null;

  // ─── Validation ────────────────────────────────────────────────
  const isValid = (): boolean => {
    if (
      !form.question_oz.trim() ||
      !form.question_uz.trim() ||
      !form.question_ru.trim()
    ) {
      return false;
    }
    if (form.choices.length < MIN_CHOICES) return false;
    if (
      form.choices.some(
        (c) => !c.text_oz.trim() || !c.text_uz.trim() || !c.text_ru.trim(),
      )
    ) {
      return false;
    }
    return true;
  };

  // ─── Variant qo'shish/o'chirish ────────────────────────────────
  const addChoice = () => {
    if (form.choices.length >= MAX_CHOICES) return;
    setForm({ ...form, choices: [...form.choices, makeChoice()] });
  };

  const removeChoice = (idx: number) => {
    if (form.choices.length <= MIN_CHOICES) return;
    const newChoices = form.choices.filter((_, i) => i !== idx);
    // To'g'ri javob indexini to'g'rilash
    let newCorrect = form.correctChoiceIdx;
    if (idx === form.correctChoiceIdx) {
      newCorrect = 0; // o'chirilgan variant to'g'ri edi → 1-chiga o'tkazamiz
    } else if (idx < form.correctChoiceIdx) {
      newCorrect = form.correctChoiceIdx - 1; // yuqoridagi o'chirildi → surilad
    }
    setForm({ ...form, choices: newChoices, correctChoiceIdx: newCorrect });
  };

  // ─── Kalit so'zlar qo'shish/o'chirish ──────────────────────────
  const addKeyword = () => {
    const value = keywordInput.trim();
    if (!value || form.keywords.includes(value)) {
      setKeywordInput("");
      return;
    }
    setForm({ ...form, keywords: [...form.keywords, value] });
    setKeywordInput("");
  };

  const removeKeyword = (idx: number) => {
    setForm({
      ...form,
      keywords: form.keywords.filter((_, i) => i !== idx),
    });
  };

  // ─── Save ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!isValid()) {
      onError("Barcha 3 tildagi maydonlarni to'ldiring");
      return;
    }
    setSaving(true);

    let ok = false;
    try {
      await adminAPI.createQuestion({
        question_oz: form.question_oz.trim(),
        question_uz: form.question_uz.trim(),
        question_ru: form.question_ru.trim(),
        // order_index — massiv tartibi bo'yicha 1, 2, 3...
        correct_answer_index: form.correctChoiceIdx + 1,
        explanation_oz: form.explanation_oz.trim() || undefined,
        explanation_uz: form.explanation_uz.trim() || undefined,
        explanation_ru: form.explanation_ru.trim() || undefined,
        topicId: form.topicId || undefined,
        question_type: form.questionType,
        keywords: form.keywords,
        choices: form.choices.map((c, i) => ({
          order_index: i + 1, // 1-based
          text_oz: c.text_oz.trim(),
          text_uz: c.text_uz.trim(),
          text_ru: c.text_ru.trim(),
        })),
      });
      ok = true;
    } catch (e: any) {
      console.error("CREATE ERROR:", e?.response?.status, e?.response?.data);
      onError(e?.response?.data?.message || "Savol qo'shishda xato");
    } finally {
      setSaving(false);
    }

    if (ok) {
      onSuccess("Savol qo'shildi. Rasm qo'shish uchun uni tahrirlang.");
      onCreated();
      onClose();
    }
  };

  // ─── Helpers ───────────────────────────────────────────────────
  const updateChoiceText = (idx: number, lang: Lang, text: string) => {
    const field = `text_${lang}` as const;
    setForm({
      ...form,
      choices: form.choices.map((c, i) =>
        i === idx ? { ...c, [field]: text } : c,
      ),
    });
  };

  const questionField = `question_${activeLang}` as keyof QuestionForm;
  const explanationField = `explanation_${activeLang}` as keyof QuestionForm;
  const choiceField = `text_${activeLang}` as const;

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 14px",
    background: "#0D1526",
    border: "1px solid #1E2D45",
    borderRadius: 10,
    color: "#E2E8F0",
    fontSize: 13,
    fontFamily: "inherit",
    outline: "none",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    color: "#64748B",
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.75)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 16,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827",
          border: "1px solid #1E2D45",
          borderRadius: 20,
          width: "100%",
          maxWidth: 600,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
        {/* HEADER */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #1E2D45",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <div>
            <h3
              style={{
                color: "#fff",
                fontWeight: 800,
                fontSize: 15,
                margin: 0,
              }}>
              Yangi savol qo'shish
            </h3>
            <p
              style={{
                color: "#475569",
                fontSize: 11,
                margin: "2px 0 0",
                fontWeight: 600,
              }}>
              Bilet avtomatik tanlanadi
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              background: "transparent",
              border: "none",
              color: "#64748B",
              cursor: saving ? "not-allowed" : "pointer",
              padding: 4,
              display: "flex",
            }}>
            <X size={20} />
          </button>
        </div>

        {/* LANG TABS */}
        <div
          style={{
            padding: "12px 24px",
            borderBottom: "1px solid #1E2D45",
            display: "flex",
            gap: 6,
          }}>
          {LANGS.map((l) => (
            <button
              key={l.key}
              onClick={() => setActiveLang(l.key)}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                border: `1px solid ${activeLang === l.key ? "#3B82F6" : "#1E2D45"}`,
                background:
                  activeLang === l.key ? "rgba(59,130,246,.12)" : "transparent",
                color: activeLang === l.key ? "#3B82F6" : "#64748B",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}>
              {l.label}
            </button>
          ))}
        </div>

        {/* BODY */}

        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {/* MAVZUNI TANLASH (CUSTOM SEARCHABLE DROPDOWN) */}
          <div style={{ marginBottom: 20, position: "relative" }}>
            <label style={labelStyle}>Mavzuni tanlang (Topic) *</label>

            {/* Bosganda dropdown ochiladigan asosiy trigger maydoni */}
            <div
              onClick={() => {
                setIsOpen(!isOpen);
                if (!isOpen) setSearchQuery(""); // Ochilganda qidiruvni tozalaydi
              }}
              style={{
                ...inputStyle,
                cursor: "pointer",
                height: 42,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderColor: isOpen ? "#3B82F6" : "#1E2D45", // Ochilganda ko'k border
                boxShadow: isOpen ? "0 0 0 2px rgba(59,130,246,0.2)" : "none",
              }}>
              <span style={{ color: selectedTopic ? "#E2E8F0" : "#64748B" }}>
                {selectedTopic ?
                  `${selectedTopic.name_oz}`
                : "Mavzuni tanlang..."}
              </span>
              {/* Lucide'dan chevron-down ikonkasini ishlatsang chiroyli chiqadi */}
              <span style={{ color: "#64748B", fontSize: 10 }}>▼</span>
            </div>

            {/* DROPDOWN RO'YXATI (FAQAT IS_OPEN TRUE BO'LGANDA CHIQADI) */}
            {isOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  marginTop: 6,
                  background: "#111827",
                  border: "1px solid #1E2D45",
                  borderRadius: 12,
                  boxShadow:
                    "0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)",
                  zIndex: 250,
                  display: "flex",
                  flexDirection: "column",
                  maxHeight: 280,
                  overflow: "hidden",
                }}>
                {/* Qidiruv inputi (Rasmda ko'rsatganingdek tepada turadi) */}
                <div
                  style={{
                    padding: 8,
                    borderBottom: "1px solid #1E2D45",
                    background: "#0D1526",
                    display: "flex",
                    alignItems: "center",
                  }}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Qidirish..."
                    autoFocus
                    onClick={(e) => e.stopPropagation()} // Input ichiga bosganda dropdown yopilib ketmasligi uchun
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      background: "#111827",
                      border: "1px solid #1E2D45",
                      borderRadius: 8,
                      color: "#E2E8F0",
                      fontSize: 13,
                      outline: "none",
                    }}
                  />
                </div>

                {/* Mavzular ro'yxati (Scroll bo'ladigan qismi) */}
                <div style={{ flex: 1, overflowY: "auto", padding: 4 }}>
                  {filteredTopics.length === 0 ?
                    <div
                      style={{
                        padding: "12px 16px",
                        color: "#64748B",
                        fontSize: 13,
                        textAlign: "center",
                      }}>
                      Mavzu topilmadi, bro
                    </div>
                  : filteredTopics.map((t) => {
                      const isCurrent = t.id === form.topicId;
                      return (
                        <div
                          key={t.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setForm({ ...form, topicId: t.id });
                            setIsOpen(false); // Tanlangach avtomatik yopiladi
                          }}
                          style={{
                            padding: "10px 14px",
                            borderRadius: 8,
                            background:
                              isCurrent ?
                                "rgba(59,130,246,0.15)"
                              : "transparent",
                            color: isCurrent ? "#3B82F6" : "#E2E8F0",
                            fontSize: 13,
                            cursor: "pointer",
                            transition: "background 0.2s",
                          }}
                          // Hover effekti uchun oddiy inline mantiq
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              isCurrent ? "rgba(59,130,246,0.2)" : "#1E2D45")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background =
                              isCurrent ?
                                "rgba(59,130,246,0.15)"
                              : "transparent")
                          }>
                          {t.name_oz}
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            )}
          </div>
          {/* CUSTOM DROPDOWN TUGADI */}
          {/* ============================================================ */}
          {/* Question */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>
              Savol matni · {LANGS.find((l) => l.key === activeLang)?.label} *
            </label>
            <textarea
              value={form[questionField] as string}
              onChange={(e) =>
                setForm({ ...form, [questionField]: e.target.value })
              }
              rows={3}
              style={{ ...inputStyle, resize: "vertical", minHeight: 80 }}
              placeholder="Savol matnini kiriting..."
            />
          </div>

          {/* Explanation */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>
              Izoh ({LANGS.find((l) => l.key === activeLang)?.label})
            </label>
            <textarea
              value={form[explanationField] as string}
              onChange={(e) =>
                setForm({ ...form, [explanationField]: e.target.value })
              }
              rows={2}
              style={{ ...inputStyle, resize: "vertical", minHeight: 60 }}
              placeholder="Izoh (ixtiyoriy)..."
            />
          </div>

          {/* Savol turi */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Savol turi</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {QUESTION_TYPES.map((qt) => {
                const active = form.questionType === qt.key;
                return (
                  <button
                    key={qt.key}
                    onClick={() => setForm({ ...form, questionType: qt.key })}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 8,
                      border: `1px solid ${active ? "#3B82F6" : "#1E2D45"}`,
                      background:
                        active ? "rgba(59,130,246,.12)" : "transparent",
                      color: active ? "#3B82F6" : "#64748B",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}>
                    {qt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Kalit so'zlar */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Kalit so'zlar</label>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginBottom: form.keywords.length > 0 ? 8 : 0,
              }}>
              {form.keywords.map((kw, idx) => (
                <span
                  key={`${kw}_${idx}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 10px",
                    borderRadius: 8,
                    background: "rgba(59,130,246,.1)",
                    border: "1px solid rgba(59,130,246,.25)",
                    color: "#3B82F6",
                    fontSize: 12,
                    fontWeight: 600,
                  }}>
                  {kw}
                  <button
                    onClick={() => removeKeyword(idx)}
                    title="O'chirish"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#3B82F6",
                      cursor: "pointer",
                      display: "flex",
                      padding: 0,
                    }}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addKeyword();
                  }
                }}
                placeholder="Kalit so'z kiriting..."
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={addKeyword}
                disabled={!keywordInput.trim()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "0 14px",
                  borderRadius: 10,
                  border: "1px solid rgba(59,130,246,.3)",
                  background:
                    !keywordInput.trim() ? "transparent" : (
                      "rgba(59,130,246,.1)"
                    ),
                  color: !keywordInput.trim() ? "#334155" : "#3B82F6",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: !keywordInput.trim() ? "not-allowed" : "pointer",
                }}>
                <Plus size={13} /> Qo'shish
              </button>
            </div>
          </div>

          {/* Choices */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>
                Variantlar · {LANGS.find((l) => l.key === activeLang)?.label} *
              </label>
              <button
                onClick={addChoice}
                disabled={form.choices.length >= MAX_CHOICES}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "4px 10px",
                  borderRadius: 8,
                  border: "1px solid rgba(59,130,246,.3)",
                  background:
                    form.choices.length >= MAX_CHOICES ?
                      "transparent"
                    : "rgba(59,130,246,.1)",
                  color:
                    form.choices.length >= MAX_CHOICES ? "#334155" : "#3B82F6",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor:
                    form.choices.length >= MAX_CHOICES ?
                      "not-allowed"
                    : "pointer",
                }}>
                <Plus size={13} /> Variant
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {form.choices.map((choice, idx) => {
                const isCorrect = idx === form.correctChoiceIdx;
                const canRemove = form.choices.length > MIN_CHOICES;
                return (
                  <div
                    key={choice._key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: 10,
                      borderRadius: 10,
                      background: isCorrect ? "rgba(34,197,94,.08)" : "#0D1526",
                      border:
                        isCorrect ?
                          "1px solid rgba(34,197,94,.3)"
                        : "1px solid #1E2D45",
                    }}>
                    {/* To'g'ri javob radio */}
                    <button
                      onClick={() =>
                        setForm({ ...form, correctChoiceIdx: idx })
                      }
                      title="To'g'ri javob"
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: isCorrect ? "#22C55E" : "#1E2D45",
                        color: isCorrect ? "#fff" : "#64748B",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 800,
                        border: "none",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}>
                      {idx + 1}
                    </button>

                    {/* Text */}
                    <input
                      value={choice[choiceField]}
                      onChange={(e) =>
                        updateChoiceText(idx, activeLang, e.target.value)
                      }
                      placeholder={`Variant ${idx + 1}`}
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        background: "transparent",
                        border: "none",
                        color: "#E2E8F0",
                        fontSize: 13,
                        fontFamily: "inherit",
                        outline: "none",
                      }}
                    />

                    {isCorrect && (
                      <CheckCircle2
                        size={16}
                        style={{ color: "#22C55E", flexShrink: 0 }}
                      />
                    )}

                    {/* O'chirish */}
                    <button
                      onClick={() => removeChoice(idx)}
                      disabled={!canRemove}
                      title={
                        canRemove ?
                          "Variantni o'chirish"
                        : `Kamida ${MIN_CHOICES} ta variant kerak`
                      }
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 7,
                        border: "1px solid rgba(239,68,68,.25)",
                        background: "rgba(239,68,68,.06)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: canRemove ? "pointer" : "not-allowed",
                        opacity: canRemove ? 1 : 0.35,
                        flexShrink: 0,
                      }}>
                      <Trash2 size={13} color="#EF4444" />
                    </button>
                  </div>
                );
              })}
            </div>
            <p
              style={{
                color: "#475569",
                fontSize: 11,
                marginTop: 8,
                fontStyle: "italic",
              }}>
              {MIN_CHOICES}–{MAX_CHOICES} ta variant. Yashil dumaloq — to'g'ri
              javob. Rasm — savol qo'shilgach Tahrirlash orqali.
            </p>
          </div>
        </div>

        {/* FOOTER */}
        <div
          style={{
            padding: "14px 24px",
            borderTop: "1px solid #1E2D45",
            display: "flex",
            gap: 10,
            background: "#0D1526",
          }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: 12,
              border: "1px solid #1E2D45",
              background: "transparent",
              color: "#E2E8F0",
              fontWeight: 700,
              fontSize: 13,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.5 : 1,
            }}>
            Bekor
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !isValid()}
            style={{
              flex: 2,
              padding: "12px",
              borderRadius: 12,
              border: "none",
              background: isValid() && !saving ? "#3B82F6" : "#1E2D45",
              color: isValid() && !saving ? "#fff" : "#64748B",
              fontWeight: 700,
              fontSize: 13,
              cursor: saving || !isValid() ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}>
            {saving ?
              <>
                <Loader2
                  size={16}
                  style={{ animation: "spin 1s linear infinite" }}
                />
                Qo'shilmoqda...
              </>
            : "Qo'shish"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
